import React, { useState } from 'react';
import { useSearchParams } from 'react-router';
import AdminLayout from '../AdminLayout';
import NewUsersChart from './components/NewUsersChart';
import ActiveUsersChart from './components/ActiveUsersChart';
import UniqueSendersChart from './components/UniqueSendersChart';
import ReferralTrafficChart from './components/ReferralTrafficChart';
import CardsAddedChart from './components/CardsAddedChart';
import PointsInEscrowChart from './components/PointsInEscrowChart';
import CardsInTransitChart from './components/CardsInTransitChart';
import PointsCreatedChart from './components/PointsCreatedChart';
import PointsDestroyedChart from './components/PointsDestroyedChart';
import EconomicBalanceChart from './components/EconomicBalanceChart';
import EscrowRatioChart from './components/EscrowRatioChart';
import TotalPointsChart from './components/TotalPointsChart';
import PointsPerUserChart from './components/PointsPerUserChart';
import TotalTradesChart from './components/TotalTradesChart';
import ActivePaidEliteChart from './components/ActivePaidEliteChart';
import EliteSignupsCancellationsChart from './components/EliteSignupsCancellationsChart';
import AvgCardValueChart from './components/AvgCardValueChart';
import InitiatedTradeValueChart from './components/InitiatedTradeValueChart';
import SmsAlertsChart from './components/SmsAlertsChart';
import CardAddSourcesCharts from './components/CardAddSourcesCharts';
import TradeInitiationSourcesChart from './components/TradeInitiationSourcesChart';
import TrialSignupsCancellationsChart from './components/TrialSignupsCancellationsChart';
import SubscriptionEventsTable from './components/SubscriptionEventsTable';
import FomoSmsChart from './components/FomoSmsChart';
import OnboardingFunnelChart from './components/OnboardingFunnelChart';
import TradeOffersByStatusChart from './components/TradeOffersByStatusChart';
import TradeOffersByUserChart from './components/TradeOffersByUserChart';
import BlocksTab from './components/BlocksTab';
import MarketingTab from './components/MarketingTab/MarketingTab';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import './AnalyticsPage.css';

type AnalyticsTab = 'growth' | 'revenue' | 'economics' | 'product' | 'blocks' | 'marketing';

const TABS: { key: AnalyticsTab; label: string }[] = [
  { key: 'growth', label: 'Growth' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'economics', label: 'Economics' },
  { key: 'product', label: 'Product' },
  { key: 'blocks', label: 'Blocks' },
  { key: 'marketing', label: 'Marketing' },
];

const isValidTab = (value: string | null): value is AnalyticsTab =>
  value === 'growth' ||
  value === 'revenue' ||
  value === 'economics' ||
  value === 'product' ||
  value === 'blocks' ||
  value === 'marketing';

const AnalyticsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AnalyticsTab = isValidTab(tabParam) ? tabParam : 'growth';

  const handleTabChange = (tab: AnalyticsTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Growth tab periods
  const [newUsersPeriod, setNewUsersPeriod] = useState<AnalyticsPeriod>(7);
  const [activeUsersPeriod, setActiveUsersPeriod] = useState<AnalyticsPeriod>(7);
  const [uniqueSendersPeriod, setUniqueSendersPeriod] = useState<AnalyticsPeriod>(7);
  const [referralTrafficPeriod, setReferralTrafficPeriod] = useState<AnalyticsPeriod>(7);
  const [cardsAddedPeriod, setCardsAddedPeriod] = useState<AnalyticsPeriod>(7);
  const [escrowPeriod, setEscrowPeriod] = useState<AnalyticsPeriod>(7);
  const [transitPeriod, setTransitPeriod] = useState<AnalyticsPeriod>(7);
  const [totalTradesPeriod, setTotalTradesPeriod] = useState<AnalyticsPeriod>(7);

  // Revenue tab periods
  const [activePaidElitePeriod, setActivePaidElitePeriod] = useState<AnalyticsPeriod>(7);
  const [eliteFlowPeriod, setEliteFlowPeriod] = useState<AnalyticsPeriod>(7);
  const [trialFlowPeriod, setTrialFlowPeriod] = useState<AnalyticsPeriod>(7);
  const [subscriptionEventsPeriod, setSubscriptionEventsPeriod] = useState<AnalyticsPeriod>(7);

  // Economics tab periods
  const [pointsCreatedPeriod, setPointsCreatedPeriod] = useState<AnalyticsPeriod>(7);
  const [pointsDestroyedPeriod, setPointsDestroyedPeriod] = useState<AnalyticsPeriod>(7);
  const [economicBalancePeriod, setEconomicBalancePeriod] = useState<AnalyticsPeriod>(7);
  const [escrowRatioPeriod, setEscrowRatioPeriod] = useState<AnalyticsPeriod>(7);
  const [totalPointsPeriod, setTotalPointsPeriod] = useState<AnalyticsPeriod>(7);
  const [pointsPerUserPeriod, setPointsPerUserPeriod] = useState<AnalyticsPeriod>(7);
  const [avgCardValuePeriod, setAvgCardValuePeriod] = useState<AnalyticsPeriod>(7);
  const [initiatedTradeValuePeriod, setInitiatedTradeValuePeriod] = useState<AnalyticsPeriod>(7);

  // Product tab periods
  const [smsAlertsPeriod, setSmsAlertsPeriod] = useState<AnalyticsPeriod>(7);
  const [cardAddSourcesPeriod, setCardAddSourcesPeriod] = useState<AnalyticsPeriod>(7);
  const [tradeInitSourcesPeriod, setTradeInitSourcesPeriod] = useState<AnalyticsPeriod>(7);
  const [fomoSmsPeriod, setFomoSmsPeriod] = useState<AnalyticsPeriod>(7);
  const [onboardingFunnelPeriod, setOnboardingFunnelPeriod] = useState<AnalyticsPeriod>(7);
  const [tradeOffersByStatusPeriod, setTradeOffersByStatusPeriod] = useState<AnalyticsPeriod>(7);
  const [tradeOffersByUserPeriod, setTradeOffersByUserPeriod] = useState<AnalyticsPeriod>(7);

  return (
    <AdminLayout title="Admin - Analytics" currentPath="/admin/dashboard">
      <div className="analytics-page">
        <div className="analytics-content">
          <h1 className="analytics-title">Admin - Analytics</h1>

          <div className="analytics-tabs" role="tablist">
            {TABS.map(tab => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`analytics-tab ${activeTab === tab.key ? 'analytics-tab-active' : ''}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'marketing' && <MarketingTab />}

          {activeTab === 'growth' && (
            <div className="analytics-charts-grid">
              <NewUsersChart
                period={newUsersPeriod}
                onPeriodChange={setNewUsersPeriod}
              />
              <ActiveUsersChart
                period={activeUsersPeriod}
                onPeriodChange={setActiveUsersPeriod}
              />
              <UniqueSendersChart
                period={uniqueSendersPeriod}
                onPeriodChange={setUniqueSendersPeriod}
              />
              <ReferralTrafficChart
                period={referralTrafficPeriod}
                onPeriodChange={setReferralTrafficPeriod}
              />
              <CardsAddedChart
                period={cardsAddedPeriod}
                onPeriodChange={setCardsAddedPeriod}
              />
              <PointsInEscrowChart
                period={escrowPeriod}
                onPeriodChange={setEscrowPeriod}
              />
              <CardsInTransitChart
                period={transitPeriod}
                onPeriodChange={setTransitPeriod}
              />
              <TotalTradesChart
                period={totalTradesPeriod}
                onPeriodChange={setTotalTradesPeriod}
              />
            </div>
          )}

          {activeTab === 'revenue' && (
            <div className="analytics-charts-grid">
              <ActivePaidEliteChart
                period={activePaidElitePeriod}
                onPeriodChange={setActivePaidElitePeriod}
              />
              <EliteSignupsCancellationsChart
                period={eliteFlowPeriod}
                onPeriodChange={setEliteFlowPeriod}
              />
              <TrialSignupsCancellationsChart
                period={trialFlowPeriod}
                onPeriodChange={setTrialFlowPeriod}
              />
              <SubscriptionEventsTable
                period={subscriptionEventsPeriod}
                onPeriodChange={setSubscriptionEventsPeriod}
              />
            </div>
          )}

          {activeTab === 'economics' && (
            <div className="analytics-charts-grid">
              <EconomicBalanceChart
                period={economicBalancePeriod}
                onPeriodChange={setEconomicBalancePeriod}
              />
              <EscrowRatioChart
                period={escrowRatioPeriod}
                onPeriodChange={setEscrowRatioPeriod}
              />
              <TotalPointsChart
                period={totalPointsPeriod}
                onPeriodChange={setTotalPointsPeriod}
              />
              <PointsPerUserChart
                period={pointsPerUserPeriod}
                onPeriodChange={setPointsPerUserPeriod}
              />
              <PointsCreatedChart
                period={pointsCreatedPeriod}
                onPeriodChange={setPointsCreatedPeriod}
              />
              <PointsDestroyedChart
                period={pointsDestroyedPeriod}
                onPeriodChange={setPointsDestroyedPeriod}
              />
              <AvgCardValueChart
                period={avgCardValuePeriod}
                onPeriodChange={setAvgCardValuePeriod}
              />
              <InitiatedTradeValueChart
                period={initiatedTradeValuePeriod}
                onPeriodChange={setInitiatedTradeValuePeriod}
              />
            </div>
          )}

          {activeTab === 'product' && (
            <div className="analytics-charts-grid">
              <TradeOffersByStatusChart
                period={tradeOffersByStatusPeriod}
                onPeriodChange={setTradeOffersByStatusPeriod}
              />
              <TradeOffersByUserChart
                period={tradeOffersByUserPeriod}
                onPeriodChange={setTradeOffersByUserPeriod}
              />
              <CardAddSourcesCharts
                period={cardAddSourcesPeriod}
                onPeriodChange={setCardAddSourcesPeriod}
              />
              <TradeInitiationSourcesChart
                period={tradeInitSourcesPeriod}
                onPeriodChange={setTradeInitSourcesPeriod}
              />
              <SmsAlertsChart
                period={smsAlertsPeriod}
                onPeriodChange={setSmsAlertsPeriod}
              />
              <FomoSmsChart
                period={fomoSmsPeriod}
                onPeriodChange={setFomoSmsPeriod}
              />
              <OnboardingFunnelChart
                period={onboardingFunnelPeriod}
                onPeriodChange={setOnboardingFunnelPeriod}
              />
            </div>
          )}

          {activeTab === 'blocks' && <BlocksTab />}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;
